import React, { createContext, useContext, ReactNode } from "react";

export interface PageActions {
  onMainAction?: () => void;
  /** A second header button, shown just before the main one (e.g. PLANNER beside ADD EVENT). */
  secondaryAction?: { label: string; onClick: () => void };
  onSync?: () => void;
  onVideoPlay?: () => void;
  onOpenInfoNotice?: () => void;
  onOpenSendImage?: () => void;
  onOpenAddEvent?: () => void;
  onOpenSendAttachment?: () => void;
  onOpenUploadDrive?: () => void;
  count?: number;
}

interface PageActionsContextType {
  actions: PageActions;
  setActions: (actions: PageActions) => void;
}

const PageActionsContext = createContext<PageActionsContextType | undefined>(
  undefined
);

interface PageActionsProviderProps {
  children: ReactNode;
}

export const PageActionsProvider: React.FC<PageActionsProviderProps> = ({
  children,
}) => {
  const [actions, setActions] = React.useState<PageActions>({});

  const setActionsStable = React.useCallback((newActions: PageActions) => {
    setActions(newActions);
  }, []);

  return (
    <PageActionsContext.Provider
      value={{ actions, setActions: setActionsStable }}
    >
      {children}
    </PageActionsContext.Provider>
  );
};

export const usePageActions = () => {
  const context = useContext(PageActionsContext);
  if (context === undefined) {
    throw new Error("usePageActions must be used within a PageActionsProvider");
  }
  return context;
};

// Hook for pages to register their actions
export const useRegisterPageActions = (actions: PageActions) => {
  const { setActions } = usePageActions();

  React.useEffect(() => {
    setActions(actions);
    return () => setActions({});
  }, [setActions, actions]);
};
